/**
 * Admin - Virtual Phone API
 *
 * Test harness for the main Nexus Twilio number: send an SMS from it and read
 * the conversation it has had with one other number.
 *
 * Base path: /api/admin/virtual-phone
 *
 * Auth: PBAC — `admin:providers` read (history) / write (send).
 *
 * ## No credential material crosses this boundary
 *
 * The Twilio account SID and auth token are read from `EnvService` inside the
 * handlers and used to call Twilio; neither is ever part of a response. `send`
 * answers with the message SID, its delivery status and a timestamp. `history`
 * answers with message metadata plus SMS bodies.
 *
 * The bodies are the reason to check rather than assume: an SMS body IS secret
 * material when the number sends one-time passcodes. This one does not.
 * `TWILIO_PHONE_NUMBER` has exactly two readers in the backend — this controller
 * and its own declaration in `env.validation.ts`, where it is documented as "Main
 * Nexus number used by the Virtual Phone test harness". Customer-facing Twilio
 * traffic runs on per-organization deployment credentials and numbers, not this
 * one, and Nexus does not deliver auth codes over it. So the conversation this
 * route reads is test traffic an admin sent themselves, and the routes carry no
 * `sensitivity` marker.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Schemas
// ============================================================

/**
 * `riskCheck: "disable"` bypasses Twilio's SMS Pumping Protection (error 30450),
 * which is the whole point of the harness — the protection blocks the very
 * test numbers one wants to send to.
 */
export const VirtualPhoneSendSmsBodySchema = z.object({
  to: z.string().min(1, "To phone number is required"),
  body: z.string().min(1, "Message body is required"),
  riskCheck: z.enum(["disable"]).optional()
});

/**
 * What Twilio reports back about the message it accepted. `status` is Twilio's
 * own value (`queued`, `sent`, `delivered`, `failed`, …) and is passed through
 * unmapped, so it stays a string rather than an enum this side would have to
 * chase.
 */
export const VirtualPhoneSendSmsResultSchema = z.object({
  messageId: z.string(),
  status: z.string(),
  sentAt: z.string()
});

/**
 * `limit` caps EACH direction of the conversation, so the response holds at most
 * `2 × limit` messages. The 200 ceiling is new with this contract: the handler
 * previously accepted any number and passed it straight to Twilio's `list`,
 * which is fine for the panel (it never sends one) and not fine for a caller
 * that puts the answer in a transcript.
 */
export const VirtualPhoneGetHistoryQuerySchema = z.object({
  to: z.string().min(1, "To phone number is required"),
  limit: z.coerce.number().int().min(1).max(200).optional()
});

/**
 * One SMS in the conversation. `direction` is derived by the client from which
 * leg of the Twilio query the message came back on, not read from Twilio.
 */
export const VirtualPhoneMessageSchema = z.object({
  id: z.string(),
  from: z.string(),
  to: z.string(),
  body: z.string(),
  direction: z.enum(["inbound", "outbound"]),
  status: z.string(),
  sentAt: z.string()
});

export const VirtualPhoneHistoryResultSchema = z.object({
  messages: z.array(VirtualPhoneMessageSchema)
});

// ============================================================
// ZAdminVirtualPhone - Endpoint Definitions
// ============================================================

export const ZAdminVirtualPhone = {
  /**
   * Send a test SMS from the main Nexus number.
   * POST /api/admin/virtual-phone/send
   */
  SendTestSms: {
    method: "POST" as const,
    path: "/api/admin/virtual-phone/send",
    Body: VirtualPhoneSendSmsBodySchema,
    Response: ApiSuccessSchema(VirtualPhoneSendSmsResultSchema)
  },

  /**
   * Read the conversation between the main Nexus number and one other number.
   * GET /api/admin/virtual-phone/history
   */
  GetHistory: {
    method: "GET" as const,
    path: "/api/admin/virtual-phone/history",
    Query: VirtualPhoneGetHistoryQuerySchema,
    Response: ApiSuccessSchema(VirtualPhoneHistoryResultSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type VirtualPhoneSendSmsBody = z.infer<typeof VirtualPhoneSendSmsBodySchema>;
export type VirtualPhoneSendSmsResult = z.infer<typeof VirtualPhoneSendSmsResultSchema>;
export type VirtualPhoneGetHistoryQuery = z.infer<typeof VirtualPhoneGetHistoryQuerySchema>;
export type VirtualPhoneMessage = z.infer<typeof VirtualPhoneMessageSchema>;
export type VirtualPhoneHistoryResult = z.infer<typeof VirtualPhoneHistoryResultSchema>;
